// ========================================================
// FAST HIT Website — CMS & FAQ Service
// ========================================================

import {
  collection,
  query,
  where,
  limit,
  getDocs,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../types/collections';

export type Announcement = {
  id: string;
  message: string;
  linkText?: string;
  linkUrl?: string;
  badge?: string;
};

export type FaqItem = {
  id: string;
  question: string;
  answer: string;
  category: 'general' | 'scoring' | 'tournaments' | 'account';
};

const DEFAULT_FAQS: FaqItem[] = [
  {
    id: 'faq-1',
    category: 'general',
    question: 'What is FAST HIT?',
    answer:
      'FAST HIT is a modern, sports-tech cricket platform designed for players, teams, leagues, and tournament organizers. It brings real-time ball-by-ball scoring, dynamic player profiles, live tournament standings, and community engagement to grassroots and competitive cricket.',
  },
  {
    id: 'faq-2',
    category: 'general',
    question: 'Can anyone view live scores without creating an account or installing the app?',
    answer:
      'Yes! FAST HIT provides full public discovery. Anyone can visit the Live Scores section on this website to follow live match commentary, review scorecards, and check tournament tables without needing an account or app download.',
  },
  {
    id: 'faq-3',
    category: 'scoring',
    question: 'Who is eligible to score a match on FAST HIT?',
    answer:
      'To preserve match integrity, scoring is strictly authorized. A match creator, tournament official, or assigned verified scorer is granted live scoring access for that fixture. Scoring events are verified and processed by trusted backend services.',
  },
  {
    id: 'faq-4',
    category: 'account',
    question: 'How do I create and manage my player profile?',
    answer:
      'Sign in through the web portal or mobile app to create your verified player card. You can configure your batting style, bowling role, bio, and privacy settings. Your match stats and achievements will automatically link to your profile.',
  },
  {
    id: 'faq-5',
    category: 'tournaments',
    question: 'How do tournament organizers set up fixtures and standings?',
    answer:
      'Organizers can create tournaments, configure overs and match formats, accept team registrations, generate groups and fixtures, and allow the system to automatically compute net run rate (NRR) and standings from verified match results.',
  },
  {
    id: 'faq-6',
    category: 'scoring',
    question: 'How can I report a dispute or incorrect scorecard?',
    answer:
      'Team captains and verified officials can submit a match dispute through the match details screen or support desk. Disputed matches are reviewed by league administrators with full event audit logs before any official revision is certified.',
  },
];

/** Fetch active CMS announcement banner (if configured in Firestore) */
export async function getActiveAnnouncement(): Promise<Announcement | null> {
  try {
    const q = query(
      collection(db, COLLECTIONS.CMS),
      where('key', '==', 'announcement'),
      where('published', '==', true),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data();
      return {
        id: snap.docs[0].id,
        message: data.body || data.title || '',
        linkText: data.linkText,
        linkUrl: data.linkUrl,
        badge: data.badge || 'New',
      };
    }
  } catch (err) {
    // Firestore CMS optional fallback
  }

  // Default platform announcement
  return {
    id: 'default-announcement',
    message: 'FAST HIT 2026 Tournament Season is officially open for registration!',
    linkText: 'Explore Tournaments',
    linkUrl: '/tournaments',
    badge: '2026 Season',
  };
}

/** Get FAQ list */
export async function getFaqList(): Promise<FaqItem[]> {
  return DEFAULT_FAQS;
}
